import { useCallback } from 'react'
import {
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    query,
    where,
    orderBy,
    Timestamp,
    serverTimestamp,
} from 'firebase/firestore'
import { firestore, auth } from '../config/firebase'
import { VehicleTransfer, VehicleTransferStatus } from '../../../shared/types/vehicle-transfer.types'
import { COLLECTIONS } from '../../../shared/constants/collections'

export interface VehicleTransferFilters {
    /** Filtruje dle UID garáže — načte převody v obou směrech (from i to). */
    garageUid?: string
    /** Filtruje dle VIN vozidla. */
    vehicleUid?: string
    /** Filtruje dle stavu — jeden status nebo pole statusů (použije 'in' operátor). */
    status?: VehicleTransferStatus | VehicleTransferStatus[]
}

type VehicleTransferUpdater = Partial<Omit<VehicleTransfer, 'id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'>>

interface IUseVehicleTransferReturn {
    get: (id: string) => Promise<VehicleTransfer | null>
    list: (filters?: VehicleTransferFilters) => Promise<VehicleTransfer[]>
    create: (data: Omit<VehicleTransfer, 'id' | 'createdAt'>) => Promise<VehicleTransfer>
    edit: (id: string, data: VehicleTransferUpdater) => Promise<void>
}

const convertDoc = (id: string, data: Record<string, unknown>): VehicleTransfer => ({
    id,
    ...data,
    createdAt: data.createdAt instanceof Timestamp
        ? data.createdAt.toDate()
        : new Date(data.createdAt as string),
    approvedAt: data.approvedAt instanceof Timestamp
        ? data.approvedAt.toDate()
        : data.approvedAt
            ? new Date(data.approvedAt as string)
            : undefined,
} as VehicleTransfer)

const useVehicleTransfer = (): IUseVehicleTransferReturn => {

    /**
     * Načte jeden záznam převodu podle jeho ID.
     * @param id - Firestore document ID převodu
     */
    const get = useCallback(async (id: string): Promise<VehicleTransfer | null> => {
        const snapshot = await getDoc(doc(firestore, COLLECTIONS.VEHICLE_TRANSFER, id))
        if (!snapshot.exists()) return null
        return convertDoc(snapshot.id, snapshot.data())
    }, [])

    /**
     * Načte seznam převodů s volitelnými filtry.
     * Výsledky jsou řazeny sestupně dle data vytvoření.
     * @param filters - Volitelné filtry: garageUid, vehicleUid, status
     */
    const list = useCallback(async (filters?: VehicleTransferFilters): Promise<VehicleTransfer[]> => {
        const col = collection(firestore, COLLECTIONS.VEHICLE_TRANSFER)
        const buildConstraints = (extra: Parameters<typeof query>[1][]) => {
            const base = [...extra]
            if (filters?.vehicleUid) base.push(where('vehicleUid', '==', filters.vehicleUid))
            if (filters?.status !== undefined) {
                if (Array.isArray(filters.status)) {
                    base.push(where('status', 'in', filters.status))
                } else {
                    base.push(where('status', '==', filters.status))
                }
            }
            base.push(orderBy('createdAt', 'desc'))
            return base
        }

        if (filters?.garageUid) {
            const [snapFrom, snapTo] = await Promise.all([
                getDocs(query(col, ...buildConstraints([where('fromGarageUid', '==', filters.garageUid)]))),
                getDocs(query(col, ...buildConstraints([where('toGarageUid', '==', filters.garageUid)]))),
            ])
            const seen = new Set<string>()
            const results: VehicleTransfer[] = []
            for (const d of [...snapFrom.docs, ...snapTo.docs]) {
                if (!seen.has(d.id)) { seen.add(d.id); results.push(convertDoc(d.id, d.data())) }
            }
            return results.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        }

        const snapshot = await getDocs(query(col, ...buildConstraints([])))
        return snapshot.docs.map((d) => convertDoc(d.id, d.data()))
    }, [])

    /**
     * Vytvoří nový záznam převodu. Pole `createdAt` je nastaveno serverem.
     * @param data - Data převodu bez `id` a `createdAt`
     */
    const create = useCallback(async (data: Omit<VehicleTransfer, 'id' | 'createdAt'>): Promise<VehicleTransfer> => {
        const ref = await addDoc(collection(firestore, COLLECTIONS.VEHICLE_TRANSFER), {
            ...data,
            createdAt: serverTimestamp(),
        })
        return { id: ref.id, ...data, createdAt: new Date() }
    }, [])

    /**
     * Aktualizuje pole existujícího záznamu převodu.
     * @param id - Firestore document ID převodu
     * @param data - Pole k aktualizaci (bez `id`, `createdAt` a `createdBy`)
     */
    const edit = useCallback(async (id: string, data: VehicleTransferUpdater): Promise<void> => {
        const uid = auth.currentUser?.uid
        if (!uid) throw new Error('Cannot update vehicle transfer without authenticated user')
        await updateDoc(doc(firestore, COLLECTIONS.VEHICLE_TRANSFER, id), {
            ...data,
            updatedAt: serverTimestamp(),
            updatedBy: uid,
        })
    }, [])

    return { get, list, create, edit }
}

export default useVehicleTransfer
